import {
  Offer,
  OfferUI,
  ProgressBarPoint,
  RollingOfferProductSequence
} from 'common/contracts';
import { ESaleDiscountDisplayType } from 'constants/enums';
import Decimal from 'decimal.js';
import { ROLLING_OFFER_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import { resolveProgressBarPoints } from 'views/Offers/utils/resolveProgressBarPoints';

type ResolvedProgressBarPoints = ReturnType<typeof resolveProgressBarPoints>;

export const enrichProgressBarPoints = (
  rawPoints: ProgressBarPoint[] | undefined,
  progressBarOffers: Offer[]
): ResolvedProgressBarPoints | undefined => {
  if (!rawPoints || rawPoints.length === 0) return undefined;
  const first = rawPoints[0];
  return resolveProgressBarPoints({
    progressBarId: first.barId === undefined ? null : first.barId,
    points: first.points === undefined ? null : first.points,
    progressBarOffers
  });
};

export const mapProductsSequence = (
  productsSequence: RollingOfferProductSequence[],
  progressBarOffers: Offer[]
) => {
  return productsSequence.map((item) => ({
    ...item,
    prices: {
      priceInUsdCents: new Decimal(item?.priceInUsdCents || 0)
        .mul(100)
        .toNumber(),
      priceDisplay: `$${item?.priceInUsdCents || 0}`,
      currencyCode: 'USD'
    },
    products: item?.products.map((productItem) => ({
      ...productItem,
      ...productItem?.product,
      images: {
        product:
          productItem?.product?.images?.find((img) => img.type === 'product')
            ?.url ||
          productItem?.product?.images?.[0]?.url ||
          null
      }
    })),
    salePercentage: item?.productSale?.sale || 0,
    salePercentageDisplayType: ESaleDiscountDisplayType.PERCENTAGE,
    badges: item?.badges || [],
    progressBarPoints: enrichProgressBarPoints(
      item?.progressBarPoints,
      progressBarOffers
    )
  }));
};

export type MappedBuyOneGetManyProduct = ReturnType<
  typeof mapProductsSequence
>[number];

export const truncateForBuyOneGetMany = (
  mappedProducts: MappedBuyOneGetManyProduct[],
  freeTilesCount?: number
): MappedBuyOneGetManyProduct[] => {
  if (!freeTilesCount || freeTilesCount <= 0) return mappedProducts;
  const desiredLength = 1 + freeTilesCount;
  if (mappedProducts.length <= desiredLength) return mappedProducts;
  return mappedProducts.slice(0, desiredLength);
};

export const reindexForBuyOneGetMany = (
  mappedProducts: MappedBuyOneGetManyProduct[]
): MappedBuyOneGetManyProduct[] => {
  const sorted = [...mappedProducts].sort((a, b) => a.index - b.index);
  return sorted.map((item, position) => ({ ...item, index: position }));
};

export interface ResolvedBuyOneGetManyDesignProps {
  backgroundImage: string | undefined;
  headerImage: string | undefined;
  backgroundOpacity: number;
  backgroundColor: ReturnType<typeof normalizeBgColor>;
  borderColor: ReturnType<typeof normalizeBgColor>;
  borderWidth: number;
  subBackgroundColor: ReturnType<typeof normalizeBgColor>;
  subBackgroundImage: string | undefined;
  lockImage: string;
  collectText: string;
  plusImage: string | undefined;
  freeTilesColumns: number | undefined;
}

export const resolveBuyOneGetManyDesignProps = (
  offerUIData?: OfferUI
): ResolvedBuyOneGetManyDesignProps => {
  const rollingOffer = offerUIData?.rollingOffer;
  return {
    backgroundImage: offerUIData?.backgroundImage,
    headerImage: rollingOffer?.headerImage,
    backgroundOpacity:
      rollingOffer?.backgroundOpacity === undefined
        ? ROLLING_OFFER_DEFAULTS.backgroundOpacity
        : rollingOffer.backgroundOpacity,
    backgroundColor:
      normalizeBgColor(rollingOffer?.backgroundColor) ||
      ROLLING_OFFER_DEFAULTS.backgroundColor,
    borderColor:
      normalizeBgColor(offerUIData?.borderColor) ||
      ROLLING_OFFER_DEFAULTS.borderColor,
    borderWidth:
      offerUIData?.borderWidth === undefined
        ? ROLLING_OFFER_DEFAULTS.borderWidth
        : offerUIData.borderWidth,
    subBackgroundColor:
      normalizeBgColor(rollingOffer?.subRollingOffer?.backgroundColor) ||
      ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
    subBackgroundImage: rollingOffer?.subRollingOffer?.backgroundImage || undefined,
    lockImage:
      rollingOffer?.subRollingOffer?.lockImage ||
      ROLLING_OFFER_DEFAULTS.lockImage,
    collectText:
      rollingOffer?.subRollingOffer?.collectText ||
      ROLLING_OFFER_DEFAULTS.collectText,
    plusImage: rollingOffer?.plusImage,
    freeTilesColumns: rollingOffer?.freeTilesLayout
  };
};
